import { useEffect, useState, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { Paginator } from "primereact/paginator";
import { formatDateWithTime } from "../../shared/utility";
import { MagnifyingGlassIcon } from "@heroicons/react/24/outline";

import { connect } from "react-redux";

import * as actions from "../../store/actions/index";
import { withRouter } from "../../shared/utility";
import { CampaignsViewModel } from "../../viewmodels/campaigns/CampaignsViewModel";
import EmptyModal from "../../components/modals/EmptyModal";
import SlideOver from "../../components/slideOver/SlideOver";
import LoadingSvg from "../../components/spinners/LoadingSvg";

function Campaigns(props) {
  const { logout, addSnackbar } = props;
  const navigate = useNavigate();
  const {
    askDeleteCampaign,
    campaigns,
    emptyModal,
    getCampaigns,
    loading,
    openCreateCampaignSlideOver,
    setEmptyModelOpen,
    setSlideOver,
    slideOver,
    fetchProductsForDropdown,
    launchCampaign,
    endCampaign,
  } = CampaignsViewModel(logout, addSnackbar);

  const searchRef = useRef();
  const [selectedCampaigns, setSelectedCampaigns] = useState([]);
  const [first, setFirst] = useState(0);
  const [rows, setRows] = useState(20);

  useEffect(() => {
    getCampaigns(null, 1);
    fetchProductsForDropdown();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSearch = () => {
    setFirst(0);
    getCampaigns(searchRef.current.value.trim() || null, 1);
  };

  const handlePagination = (event) => {
    setFirst(event.first);
    setRows(event.rows);
    const nextPage = event.page + 1;
    getCampaigns(searchRef.current.value.trim() || null, nextPage);
  };

  const statusBody = useCallback((rowData) => {
    if (rowData.active == 1) {
      return <span className="text-green-600">Active</span>;
    } else if (rowData.active == 2) {
      return <span className="text-red-600">Inactive</span>;
    } else {
      return <span className="text-gray-600">Not Launched</span>;
    }
  }, []);

  const startDateBody = useCallback((rowData) => {
    if (rowData.campaign_start == null) {
      return "N/A";
    }
    return formatDateWithTime(rowData.campaign_start);
  }, []);

  const endDateBody = useCallback((rowData) => {
    if (rowData.campaign_end == null) {
      return "N/A";
    }
    return formatDateWithTime(rowData.campaign_end);
  }, []);

  const actionBody = useCallback(
    (rowData) => {
      return (
        <div className="flex gap-2">
          {rowData.active == 0 ? (
            <button
              type="button"
              className="rounded-md bg-orange-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-orange-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-orange-600"
              onClick={async () => {
                await launchCampaign(rowData.id);
                navigate(`/campaigns/${rowData.id}/view`);
              }}
            >
              Launch
            </button>
          ) : (
            <button
              type="button"
              className="rounded-md bg-orange-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-orange-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-orange-600"
              onClick={() => navigate(`/campaigns/${rowData.id}/view`)}
            >
              Launch
            </button>
          )}

          <button
            type="button"
            className="rounded-md bg-main-purple px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-indigo-600 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600"
            onClick={() => navigate(`/campaigns/${rowData.id}`)}
          >
            Edit
          </button>
          <button
            type="button"
            className="rounded-md bg-red-600 px-3 py-1.5 text-sm font-semibold text-white shadow-sm hover:bg-red-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-red-600"
            onClick={() => askDeleteCampaign(rowData)}
          >
            Delete
          </button>
        </div>
      );
    },
    [askDeleteCampaign, navigate],
  );

  const handleSelectionChange = useCallback((e) => {
    setSelectedCampaigns(e.value);
  }, []);

  return (
    <div>
      <div className="mt-2">
        <div className="px-4 sm:px-6 lg:px-8">
          <div className="sm:flex sm:items-center px-4 py-5 card-custom">
            <div className="sm:flex-auto">
              <h1 className="text-base font-semibold leading-6 text-gray-900">
                Campaigns
              </h1>
              <div className="mt-5 flex rounded-md">
                <div className="relative flex items-stretch flex-grow focus-within:z-10">
                  <input
                    type="search"
                    name="searchCampaigns"
                    id="searchCampaigns"
                    className="block w-full rounded-lg rounded-l-md pl-2 sm:text-sm border border-gray-300 rounded-tr-none rounded-br-none"
                    placeholder="Search by name"
                    ref={searchRef}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") handleSearch();
                    }}
                  />
                </div>
                <button
                  type="button"
                  className="-ml-px relative inline-flex items-center space-x-2 px-4 py-2 border border-gray-300 text-sm font-medium rounded-r-md text-gray-700 bg-gray-50 hover:bg-gray-100 focus:outline-none focus:ring-1"
                  onClick={() => handleSearch()}
                >
                  <MagnifyingGlassIcon
                    className="h-5 w-5 text-gray-400"
                    aria-hidden="true"
                  />
                </button>
                <button
                  type="button"
                  className="ml-5 rounded-md bg-main-purple px-2 py-2 text-sm font-semibold text-white shadow-sm hover:bg-indigo-600 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600"
                  onClick={openCreateCampaignSlideOver}
                >
                  Add Campaign
                </button>
              </div>
            </div>
          </div>

          <div className="mt-8 flow-root overflow-x-auto">
            <div className="inline-block min-w-full py-2 align-middle">
              {loading ? (
                <div className="border h-10 shadow rounded-md p-4 w-full mx-auto flex items-center">
                  <LoadingSvg />
                  <span className="text-gray-600">Loading</span>
                </div>
              ) : (
                <>
                  <div className="overflow-hidden card-custom">
                    {!campaigns.data || campaigns.data.length === 0 ? (
                      <div className="px-4 py-8 text-center text-gray-500 text-sm">
                        Currently no campaigns
                      </div>
                    ) : (
                      <DataTable
                        value={campaigns.data}
                        selection={selectedCampaigns}
                        onSelectionChange={handleSelectionChange}
                        selectionMode="checkbox"
                        dataKey="id"
                        tableClassName="min-w-full bg-white divide-y divide-gray-300 text-sm"
                        cellClassName="p-2 bg-white border-1-bottom-gray text-sm"
                        resizableColumns={false}
                        sortable={false}
                      >
                        <Column
                          selectionMode="multiple"
                          headerStyle={{ width: "5%" }}
                        />
                        <Column
                          field="name"
                          header="Store Name"
                          headerStyle={{ width: "15%" }}
                        />
                        <Column
                          field="address"
                          header="Address"
                          headerStyle={{ width: "15%" }}
                        />

                        <Column
                          header="Status"
                          body={statusBody}
                          headerStyle={{ width: "15%" }}
                        />

                        <Column
                          header="Start Date"
                          body={startDateBody}
                          headerStyle={{ width: "20%" }}
                        />
                        <Column
                          header="End Date"
                          body={endDateBody}
                          headerStyle={{ width: "20%" }}
                        />
                        <Column
                          header=""
                          body={actionBody}
                          headerStyle={{ width: "25%" }}
                        />
                      </DataTable>
                    )}
                  </div>
                  <div className="w-full">
                    <Paginator
                      first={first}
                      rows={rows}
                      totalRecords={campaigns.total ?? 0}
                      onPageChange={handlePagination}
                    />
                  </div>
                </>
              )}
            </div>
          </div>

          <EmptyModal
            childComponent={emptyModal.childComponent}
            open={emptyModal.open}
            setOpen={setEmptyModelOpen}
          />
          <SlideOver
            childComponent={slideOver.childComponent}
            slideOver={slideOver}
            setSlideOver={setSlideOver}
          />
        </div>
      </div>
    </div>
  );
}

const mapStateToProps = () => ({});

const mapDispatchToProps = (dispatch) => ({
  logout: () => dispatch(actions.logout()),
  addSnackbar: (snackbar) => dispatch(actions.addSnackbar(snackbar)),
});

export default withRouter(
  connect(mapStateToProps, mapDispatchToProps)(Campaigns),
);
